import React, { useState } from "react";
import { useSiteContent } from "../../context/SiteContentContext";
import { apiUrl, sendPaymentSlip } from "../../services/newspaperApi";
import { formatDateTime } from "../../utils/dateFormat";

// "ชำระค่าลงประกาศ" (owner 28-10-2569): PromptPay QR + bank account the admin set on /admin/editor (site content
// paymentInfo), the price, and a slip upload. Shown after submitting and on the status page while the notice waits for
// payment. The phone box is filled when the page already knows it (it must match the one used when submitting).
const SLIP_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const MAX_SLIP = 10 * 1024 * 1024;

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value).replace(/[\s-]/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the number stays visible */
    }
  };
  return (
    <button type="button" className="text-link" onClick={copy}>
      {copied ? "คัดลอกแล้ว ✓" : "คัดลอก"}
    </button>
  );
}

// trackingCode = one notice, or an order code ("OD…") for several notices paid together; amount = what to pay
function PaymentBox({ trackingCode, phone: knownPhone = "", slipCount = 0, slipAt = null, amount = null }) {
  const { siteContent } = useSiteContent();
  const p = siteContent.paymentInfo || {};
  const due = amount ?? (Number(p.pricePerNotice ?? 50) || null);
  const price = due ? `ยอดที่ต้องชำระ ${Number(due).toLocaleString("th-TH")} บาท` : (siteContent.submitPage || {}).priceNote;
  const [phone, setPhone] = useState(knownPhone);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(slipCount > 0 ? { count: slipCount, at: slipAt } : null);

  const hasQr = Boolean(p.qrUrl || p.promptPayId);
  const hasBank = Boolean(p.accountNo);

  const pick = (e) => {
    const f = e.target.files?.[0] || null;
    setError("");
    if (f && !/\.(jpe?g|png|pdf)$/i.test(f.name)) { setError("สลิปต้องเป็นรูป JPG / PNG หรือไฟล์ PDF"); setFile(null); return; }
    if (f && f.size > MAX_SLIP) { setError("ไฟล์สลิปใหญ่เกิน 10 MB"); setFile(null); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError("กรุณาเลือกไฟล์สลิป"); return; }
    setBusy(true);
    setError("");
    try {
      const res = await sendPaymentSlip(trackingCode, phone.trim(), file);
      setSent({ count: res.slipCount, at: res.slipAt });
      setFile(null);
      e.target.reset();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="payment-box" aria-label="ชำระค่าลงประกาศ">
      <p className="eyebrow">{p.eyebrow || "PAYMENT"}</p>
      <h3>{p.title}</h3>
      {price && <p className="payment-price"><strong>{price}</strong></p>}

      {hasQr || hasBank ? (
        <>
          <p>{p.text}</p>
          <div className="payment-channels">
            {hasQr && (
              <div className="payment-channel">
                <span>พร้อมเพย์ (PromptPay)</span>
                {p.qrUrl && <img src={apiUrl(p.qrUrl)} alt="QR พร้อมเพย์สำหรับชำระค่าลงประกาศ" />}
                {p.promptPayId && (
                  <p className="payment-number">
                    <strong>{p.promptPayId}</strong> <CopyButton value={p.promptPayId} />
                  </p>
                )}
                {p.promptPayName && <small>ชื่อบัญชี {p.promptPayName}</small>}
              </div>
            )}
            {hasBank && (
              <div className="payment-channel">
                <span>โอนเข้าบัญชีธนาคาร</span>
                {p.bankName && <p>{p.bankName}</p>}
                <p className="payment-number">
                  <strong>{p.accountNo}</strong> <CopyButton value={p.accountNo} />
                </p>
                {p.accountName && <small>ชื่อบัญชี {p.accountName}</small>}
              </div>
            )}
          </div>
        </>
      ) : (
        <p>{p.emptyText}</p>
      )}

      <div className="payment-slip">
        <h4>{p.slipTitle}</h4>
        {sent && (
          <p className="payment-sent" role="status">
            ✓ {p.slipDoneText} ({sent.count} ไฟล์{sent.at ? ` · ล่าสุด ${formatDateTime(sent.at)}` : ""})
          </p>
        )}
        <form className="site-form" onSubmit={submit}>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="form-row">
            {!knownPhone && (
              <label>
                เบอร์โทรที่ใช้ตอนส่งประกาศ <span className="required">*</span>
                <input required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
              </label>
            )}
            <label>
              ไฟล์สลิป <span className="required">*</span>
              <input type="file" accept={SLIP_ACCEPT} onChange={pick} />
              <small>{p.slipNote}</small>
            </label>
          </div>
          <button className="button dark" type="submit" disabled={busy || !file}>
            {busy ? "กำลังส่งสลิป..." : sent ? "แนบสลิปเพิ่ม" : "ส่งสลิปแจ้งชำระเงิน"}{" "}
            <span>↗</span>
          </button>
        </form>
      </div>
    </section>
  );
}

export default PaymentBox;
